import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { StrategicGridData } from 'features/strategicAnalysis/types/common';
import { StrategicAnalysisAttainmentProfile } from 'features/strategicAnalysis/types/attainmentProfile';
import { resetSessionOnGradepointChange, clearSessionData, setStrategicLoading } from '../slice';
/**
 * Initial State
 */
const initialState: StrategicAnalysisAttainmentProfile.State = {
	examLevels: undefined,
	grids: undefined,
	rows: undefined,
	comparisons: undefined,
	error: '',
	loading: false,
	showThermometerToggle: false,
	embeddedTableLoading: false,
	embeddedTable: {
		activeEmbeddedTableId: undefined,
		examLevels: undefined,
		grids: undefined,
		rows: undefined,
	},
};
/**
 * Reducers / Actions
 */
const strategicAnalysisAttainmentProfile = createSlice({
	name: 'strategicAnalysisAttainmentProfile',
	initialState,
	reducers: {
		setMainGridDataSuccess(
			state: StrategicAnalysisAttainmentProfile.State,
			action: PayloadAction<StrategicGridData.GridData>
		) {
			const { examLevels, grids, rows } = action.payload;
			// If this is the first fetch just set the data
			if (!state.examLevels) {
				return {
					...state,
					examLevels,
					grids,
					rows,
				};
			}
			// If this is a subsequent fetch update the data
			return {
				...state,
				examLevels: {
					...state.examLevels,
					id: examLevels.id,
					byId: {
						...state.examLevels!.byId,
						...examLevels.byId,
					},
					//@ts-ignore bloody spread error again
					allIds: [...state.examLevels.allIds, ...examLevels.allIds],
				},
				grids: {
					...state.grids,
					id: grids.id,
					byId: {
						...state.grids?.byId,
						...grids.byId,
					},
					allIds: [...state.grids!.allIds, ...grids.allIds],
				},
				rows: {
					...state.rows,
					id: rows.id,
					byId: {
						...state.rows!.byId,
						...rows.byId,
					},
					allIds: [...state.rows!.allIds, ...rows.allIds],
				},
				loading: false,
			};
		},
		setMainGridDataFail(
			state: StrategicAnalysisAttainmentProfile.State,
			action: PayloadAction<string>
		) {
			const { payload } = action;

			return {
				...state,
				error: payload,
				loading: false,
			};
		},
		setAllComparisonDataSuccess(
			state: StrategicAnalysisAttainmentProfile.State,
			action: PayloadAction<
				StrategicGridData.Comparisons<StrategicAnalysisAttainmentProfile.ComparisonRow>
			>
		) {
			const { payload } = action;

			if (!state.rows || !payload) {
				return {
					...state,
					loading: false,
				};
			}

			return {
				...state,
				comparisons: !state.comparisons
					? payload
					: {
							...state.comparisons,
							id: payload.id,
							byId: {
								...state.comparisons.byId,
								...payload.byId,
							},
							allIds: Array.from(new Set([...state.comparisons.allIds, ...payload.allIds])),
					  },
				rows: {
					...state.rows,
					byId: Object.entries(state.rows.byId).reduce((acc, [key, val]) => {
						return {
							...acc,
							[key]: {
								...val,
								comparisonIds: Array.from(
									new Set(
										Object.values(payload.byId)
											.filter((val) => val.rowId === key)
											.map((comparison) => comparison.comparisonId)
									)
								),
							},
						};
					}, {}),
				},
				loading: false,
			};
		},
		setAllRowComparisonIds(
			state: StrategicAnalysisAttainmentProfile.State,
			action: PayloadAction<string[]>
		) {
			const { payload } = action;

			if (!state.rows || !payload) {
				return {
					...state,
					loading: false,
				};
			}

			return {
				...state,
				rows: {
					...state.rows,
					byId: Object.entries(state.rows!.byId).reduce((acc, [key, val]) => {
						return {
							...acc,
							[key]: {
								...val,
								comparisonIds:
									payload.length > 0
										? payload.filter((comparisonId) => comparisonId.includes(`${val.rowId}-`))
										: [],
							},
						};
					}, {}),
				},
				loading: false,
			};
		},
		setComparisonDataFail(
			state: StrategicAnalysisAttainmentProfile.State,
			action: PayloadAction<string>
		) {
			const { payload } = action;

			return {
				...state,
				error: payload,
				loading: false,
			};
		},
		setEmbeddedTableDataSuccess(
			state: StrategicAnalysisAttainmentProfile.State,
			action: PayloadAction<StrategicGridData.GridData & { activeEmbeddedTableId: string }>
		) {
			const { examLevels, grids, rows, activeEmbeddedTableId } = action.payload;

			// If this is the first fetch just set the data
			if (!state.embeddedTable.examLevels) {
				return {
					...state,
					embeddedTableLoading: false,
					embeddedTable: {
						activeEmbeddedTableId,
						examLevels,
						grids,
						rows,
					},
				};
			}
			// If this is a subsequent fetch update the data
			return {
				...state,
				embeddedTableLoading: false,
				embeddedTable: {
					activeEmbeddedTableId,
					examLevels: {
						...state.embeddedTable.examLevels,
						id: examLevels.id,
						byId: {
							...state.embeddedTable.examLevels!.byId,
							...examLevels.byId,
						},
						//@ts-ignore bloody spread error again
						allIds: [...state.embeddedTable.examLevels.allIds, ...examLevels.allIds],
					},
					grids: {
						...state.embeddedTable.grids,
						id: grids.id,
						byId: {
							...state.embeddedTable.grids?.byId,
							...grids.byId,
						},
						allIds: [...state.embeddedTable.grids!.allIds, ...grids.allIds],
					},
					rows: {
						...state.embeddedTable.rows,
						id: rows.id,
						byId: {
							...state.embeddedTable.rows!.byId,
							...rows.byId,
						},
						allIds: [...state.embeddedTable.rows!.allIds, ...rows.allIds],
					},
				},
			};
		},
		setEmbeddedTableDataFail(
			state: StrategicAnalysisAttainmentProfile.State,
			action: PayloadAction<string>
		) {
			const { payload } = action;

			return {
				...state,
				embeddedTableLoading: false,
				error: payload,
			};
		},
		setLoadingEmbeddedLoading(
			state: StrategicAnalysisAttainmentProfile.State,
			action: PayloadAction<boolean>
		) {
			const { payload } = action;

			return {
				...state,
				embeddedTableLoading: payload,
			};
		},
	},
	extraReducers: (builder) => {
		builder.addCase(
			setStrategicLoading,
			(state: StrategicAnalysisAttainmentProfile.State, action: PayloadAction<boolean>) => {
				return {
					...state,
					loading: action.payload,
				};
			}
		);
		builder.addCase(resetSessionOnGradepointChange, () => {
			return initialState;
		});
		builder.addCase(clearSessionData, () => {
			return initialState;
		});
	},
});

export const {
	setMainGridDataSuccess,
	setMainGridDataFail,
	setAllComparisonDataSuccess,
	setAllRowComparisonIds,
	setComparisonDataFail,
	setEmbeddedTableDataSuccess,
	setEmbeddedTableDataFail,
	setLoadingEmbeddedLoading,
} = strategicAnalysisAttainmentProfile.actions;

export default strategicAnalysisAttainmentProfile.reducer;
